import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Textarea,
} from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  FileTextIcon,
  InfoIcon,
  UserIcon,
  ZapIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ReportDetail, TargetPreview } from "../api/client";
import { useReportQuery, useUpdateReportStatusMutation } from "../api/queries";
import { ReportDetailToolbar } from "../ui/ReportDetailToolbar";

const STATUS_LABELS: Record<string, string> = {
  open: "ເປີດ",
  reviewing: "ກຳລັງກວດສອບ",
  actioned: "ດຳເນີນການແລ້ວ",
  dismissed: "ປິດລາຍງານ",
};

const TARGET_TYPE_LABELS: Record<string, string> = {
  listing: "ລາຍການສິນຄ້າ",
  review: "ຄຳແນະນຳ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງແປງ",
  job: "ວຽກ",
};

function TargetPreviewBlock({
  preview,
  targetId,
}: {
  preview: TargetPreview | null;
  targetId: string;
}) {
  if (!preview) {
    return (
      <p className="text-muted-foreground text-sm">
        ບໍ່ພົບຂໍ້ມູນເປົ້າໝາຍ (ID: {targetId})
      </p>
    );
  }

  if (preview.type === "listing") {
    return (
      <div className="space-y-1">
        <p className="font-medium">{preview.productName}</p>
        <p className="text-muted-foreground text-sm">{preview.shopName}</p>
        <Link
          to="/app/moderation/listings/$id"
          params={{ id: preview.id }}
          className="text-primary text-sm hover:underline"
        >
          ເບິ່ງລາຍການສິນຄ້າ →
        </Link>
      </div>
    );
  }

  if (preview.type === "review") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <span className="text-amber-500">
            {"★".repeat(Math.round(preview.rating))}
          </span>
          <span className="text-muted-foreground text-sm">
            ({preview.rating}/5)
          </span>
        </div>
        {preview.content && (
          <p className="border-muted border-l-2 pl-2 text-sm italic">
            {preview.content}
          </p>
        )}
        {preview.listingId && (
          <Link
            to="/app/moderation/listings/$id"
            params={{ id: preview.listingId }}
            className="text-primary text-sm hover:underline"
          >
            ເບິ່ງລາຍການ # {preview.listingId.slice(-8)} →
          </Link>
        )}
      </div>
    );
  }

  if (preview.type === "shop") {
    return (
      <div className="space-y-1">
        <p className="font-medium">{preview.name}</p>
        <p className="text-muted-foreground text-sm">ລະຫັດຮ້ານ: {preview.id}</p>
      </div>
    );
  }

  if (preview.type === "mechanic") {
    return (
      <div className="space-y-1">
        <p className="font-medium">{preview.name ?? `ຊ່າງແປງ #${preview.id}`}</p>
        <p className="text-muted-foreground text-sm">ລະຫັດ: {preview.id}</p>
      </div>
    );
  }

  return <p className="text-muted-foreground text-sm">ລະຫັດວຽກ: {preview.id}</p>;
}

function ReportDetailContent({ report }: { report: ReportDetail }) {
  const nav = useNavigate({ from: "/app/moderation/reports/$id" });
  const { id } = useParams({ from: "/app/moderation/reports/$id" });
  const updateStatus = useUpdateReportStatusMutation(id);
  const [actionTaken, setActionTaken] = useState("");

  const canResolve = report.status === "reviewing";
  const statusLabel = STATUS_LABELS[report.status] ?? report.status;
  const targetTypeLabel =
    TARGET_TYPE_LABELS[report.targetType] ?? report.targetType;

  const handleConfirmAction = async () => {
    const text = actionTaken.trim();
    if (!text) return;
    await updateStatus.mutateAsync({ action: "actioned", actionTaken: text });
    nav({ to: "/app/moderation/reports" });
  };

  const handleDismiss = async () => {
    const text = actionTaken.trim();
    if (!text) return;
    await updateStatus.mutateAsync({ action: "dismissed", actionTaken: text });
    nav({ to: "/app/moderation/reports" });
  };

  return (
    <>
      <Header>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => nav({ to: "/app/moderation/reports" })}
            aria-label="ກັບຄືນ"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ການກວດກາຕະຫຼາດ", to: "/app/moderation" },
              { label: "ລາຍງານ", to: "/app/moderation/reports" },
              { label: "ລາຍລະອຽດ & ການແກ້ໄຂ" },
            ]}
          />
        </div>
      </Header>

      <Main>
        <ReportDetailToolbar
          reportId={report.id}
          status={report.status}
          statusLabel={statusLabel}
        />

        <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
          {/* Left column */}
          <div className="space-y-6">
            <Card className="gap-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileTextIcon className="h-4 w-4" />
                  ສະຫຼຸບລາຍງານ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  ສົ່ງເມື່ອ {formatDateTimeLocal(report.createdAt)}
                </p>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                    ຂໍ້ມູນຜູ້ລາຍງານ
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <UserIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {report.reporterName ?? "—"}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        ລະຫັດຜູ້ໃຊ້: #{report.reporterUserId.slice(-8)}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                    ເຫດຜົນການລາຍງານ
                  </p>
                  <p className="font-medium">{report.reason ?? "—"}</p>
                  <p className="text-muted-foreground text-sm">
                    ປະເພດທີ່ຖືກລາຍງານ: {targetTypeLabel}
                  </p>
                </div>
                {report.details && (
                  <div>
                    <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                      ຄຳເຫັນຜູ້ລາຍງານ
                    </p>
                    <blockquote className="border-muted border-l-2 pl-3 text-sm italic">
                      {report.details}
                    </blockquote>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="gap-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircleIcon className="h-4 w-4 text-[#F26522]" />
                  ຕົວຢ່າງເປົ້າໝາຍ: {targetTypeLabel}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TargetPreviewBlock
                  preview={report.targetPreview}
                  targetId={report.targetId}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <RequirePermissions all={["reports:update"]}>
              <Card className="gap-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ZapIcon className="h-4 w-4 text-primary" />
                    ການດຳເນີນການແກ້ໄຂ
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="ກະລຸນາບັນທຶກເຫດຜົນການຕັດສິນກວດກາໃຫ້ລະອຽດ..."
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    rows={4}
                    className="resize-none"
                    disabled={!canResolve}
                  />
                  {canResolve && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleConfirmAction}
                        disabled={!actionTaken.trim() || updateStatus.isPending}
                      >
                        ຢືນຢັນການດຳເນີນການ
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleDismiss}
                        disabled={!actionTaken.trim() || updateStatus.isPending}
                      >
                        ປິດລາຍງານ
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </RequirePermissions>

            <Card className="gap-0">
              <CardHeader>
                <CardTitle className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  ປະຫວັດກ່ອນໜ້າ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>ຈຳນວນລາຍງານທັງໝົດ: {report.reporterTotalReports ?? "—"}</p>
                <p>
                  ອັດຕາການດຳເນີນການ:{" "}
                  {report.reporterActionRate != null
                    ? `${report.reporterActionRate}%`
                    : "—"}
                </p>
                <div className="mt-2 flex gap-2 rounded-md border bg-muted/30 p-2">
                  <InfoIcon className="h-4 w-4 shrink-0 text-[#F26522]" />
                  <p className="text-muted-foreground text-xs">
                    ການຕັດສິນຈະຖືກບັນທຶກ ແລະ ສາມາດເບິ່ງໄດ້ໂດຍຜູ້ກວດກາອາວຸໂສ.
                    ຈະສົ່ງການແຈ້ງເຕືອນໄປຫາຜູ້ລາຍງານ ແລະ ຜູ້ຖືກລາຍງານອັດຕະໂຍບາຍຫຼັງຢືນຢັນ.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="gap-0">
              <CardHeader>
                <CardTitle className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  ຂໍ້ມູນເທັກນິກ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-muted-foreground text-sm">
                <p>ເວທີ: {report.auditMetadata?.platform ?? "—"}</p>
                <p>ທີ່ຢູ່ IP: {report.auditMetadata?.ip ?? "—"}</p>
                <p>ເວັບເບີ້ວເຊີ: {report.auditMetadata?.userAgent ?? "—"}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>
    </>
  );
}

export function ReportDetailPage() {
  const { id } = useParams({ from: "/app/moderation/reports/$id" });
  const { data, isLoading, error } = useReportQuery(id);

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header />
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            ບໍ່ພົບລາຍງານ ຫຼື ເກີດຂໍ້ຜິດພາດ
          </div>
        </Main>
      </>
    );
  }

  return <ReportDetailContent report={data} />;
}
